import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Links an Object Intelligence (or other) result to a BOQ item + material as an auditable
// QuantityObservation. AI counts are NOT written to planned quantities — they await review.
const LogQuantityEvidence = ({ projectId, aiQuantity, sourceType, sourceId, unit, constructionContext,
  observationType = 'VISUAL_COUNT', measurementType, onClose, onLogged }) => {
  const [boqs, setBoqs] = useState([]);
  const [boqId, setBoqId] = useState('');
  const [items, setItems] = useState([]);
  const [boqItemId, setBoqItemId] = useState('');
  const [materials, setMaterials] = useState([]);
  const [materialId, setMaterialId] = useState('');
  const [qty, setQty] = useState(aiQuantity ?? '');
  const [obsUnit, setObsUnit] = useState(unit || 'NOS');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/boqs`, { params: { status: 'ACTIVE' } }).then((r) => { setBoqs(r.data); if (r.data[0]) setBoqId(r.data[0].id); }).catch(() => {});
    axios.get(`${API}/projects/${projectId}/materials-master`).then((r) => setMaterials(r.data)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!boqId) { setItems([]); return; }
    axios.get(`${API}/projects/${projectId}/boqs/${boqId}/items`, { params: { pageSize: 500 } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [projectId, boqId]);

  const submit = async () => {
    if (!boqItemId) { toast.error('Select a BOQ item'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/quantity-observations`, {
        boqId, boqItemId, materialId: materialId || null,
        observationType, sourceType: sourceType || 'IMAGE', sourceId: sourceId || null,
        measurementType: measurementType || null,
        clientKey: (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${sourceId || 'obs'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        observedQuantity: Number(qty), aiOriginalQuantity: observationType === 'VISUAL_COUNT' ? (aiQuantity ?? null) : null, unit: obsUnit,
        constructionContext: constructionContext && Object.keys(constructionContext).length ? constructionContext : null,
      });
      toast.success('Logged as quantity evidence');
      onLogged && onLogged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log evidence'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="log-evidence-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-slate-900 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-blue-600" /> Log Quantity Evidence</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Active BOQ</label>
            <select value={boqId} onChange={(e) => setBoqId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="evidence-boq-select">
              {boqs.length === 0 && <option value="">No active BOQ</option>}
              {boqs.map((b) => <option key={b.id} value={b.id}>{b.name} — {b.revision}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">BOQ Item</label>
            <select value={boqItemId} onChange={(e) => setBoqItemId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="evidence-item-select">
              <option value="">— select —</option>
              {items.map((i) => <option key={i.id} value={i.id}>{i.itemCode ? `${i.itemCode} · ` : ''}{i.description}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Material (optional)</label>
            <select value={materialId} onChange={(e) => setMaterialId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="evidence-material-select">
              <option value="">— none —</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Observed Quantity {observationType === 'VISUAL_COUNT' && aiQuantity != null && <span className="text-slate-400">(AI: {aiQuantity})</span>}</label>
              <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="evidence-qty-input" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Unit</label>
              <input value={obsUnit} onChange={(e) => setObsUnit(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="evidence-unit-input" />
            </div>
          </div>
          <p className="text-xs text-slate-400">{observationType === 'VISUAL_COUNT' && (sourceType || 'IMAGE') !== 'MANUAL' ? 'AI-derived evidence is created as PENDING_REVIEW and won\'t change planned quantities until reviewed.' : 'This creates an auditable quantity observation. Units must be dimensionally compatible with the BOQ item.'}</p>
          <button onClick={submit} disabled={saving} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="evidence-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Evidence'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogQuantityEvidence;
